import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, X, Wallet } from "lucide-react";
import api, { formatError } from "@/lib/api";

const PILOT_MONTHS = ["2026-06", "2026-07", "2026-08", "2026-09", "2026-10", "2026-11", "2026-12"];
const NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const label = (m) => `${NAMES[Number(m.slice(5)) - 1]} ${m.slice(0, 4)}`;
const today = () => new Date().toISOString().slice(0, 10);

export default function PaymentControl({ parent, onChange }) {
  const pay = parent.payment || {};
  const [month, setMonth] = useState(pay.due_month || PILOT_MONTHS[0]);
  const [paidAt, setPaidAt] = useState(today());
  const [busy, setBusy] = useState(false);
  const id = parent.email;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/parents/${parent.id}/payments`, { month, paid_at: paidAt });
      onChange(data.payment);
      toast.success(`Marked ${label(month)} as paid`);
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove the ${label(m)} payment?`)) return;
    try {
      const { data } = await api.delete(`/admin/parents/${parent.id}/payments/${m}`);
      onChange(data.payment);
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail));
    }
  };

  return (
    <div className="mt-3 border-t border-slate-700 pt-3" data-testid={`admin-payment-${id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Wallet size={15} className="text-yellow-300" />
        {pay.due_month && (
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${pay.due_paid ? "bg-green-900/50 text-green-300" : "bg-amber-900/50 text-amber-300"}`} data-testid={`admin-payment-due-${id}`}>
            {pay.due_label}: {pay.due_paid ? "paid" : "not paid yet"}
          </span>
        )}
        <span className="text-slate-400 text-xs" data-testid={`admin-payment-last-${id}`}>
          Last paid: {pay.last_paid ? `${label(pay.last_paid.month)} (on ${pay.last_paid.paid_at})` : "none yet"}
        </span>
        {pay.reference && <span className="text-slate-500 text-xs">· Ref: <span className="text-slate-300">{pay.reference}</span></span>}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <span className="text-xs text-slate-400">Paid for</span>
        <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid={`admin-payment-month-${id}`} className="h-8 px-2 rounded-lg bg-slate-900 border-2 border-slate-700 text-xs">
          {PILOT_MONTHS.map((m) => <option key={m} value={m}>{label(m)}</option>)}
        </select>
        <span className="text-xs text-slate-400">on</span>
        <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} data-testid={`admin-payment-date-${id}`} className="h-8 px-2 rounded-lg bg-slate-900 border-2 border-slate-700 text-xs [color-scheme:dark]" />
        <button onClick={save} disabled={busy || !paidAt} data-testid={`admin-payment-save-${id}`} className="h-8 px-3 rounded-full bg-green-500 text-slate-900 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50">
          <CheckCircle2 size={13} /> Mark paid
        </button>
      </div>
      {pay.history?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`admin-payment-history-${id}`}>
          {pay.history.slice(0, 6).map((h) => (
            <span key={h.month} className="inline-flex items-center gap-1 rounded-full bg-slate-900 border border-slate-600 pl-2 pr-1 py-0.5 text-xs">
              {label(h.month)} · {h.paid_at}
              <button onClick={() => remove(h.month)} title="Remove" data-testid={`admin-payment-remove-${id}-${h.month}`} className="text-slate-500 hover:text-red-400"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
